import { useEffect, useState } from 'react'
import {
  api, ApiError,
  type CollectionItem, type RunResult, type ShelfRow,
} from '../lib/api'
import { day, useDialogGuard, useReturnFocus } from '../lib/sheet'

/** Going to collect what was left at an address — `PLAN.html` §07.
 *
 *  A dispatcher sends somebody to an address, hands them a list of what is
 *  there, and ticks off what actually came back in the van.
 *
 *  TWO STEPS, AND THE FIRST ONE WRITES NOTHING. Opening this reads what is
 *  open at the address and shows it; "Start collection" is what snapshots the
 *  checklist into `collection_items` on the server. It is the shape
 *  `migrate --apply` and every console Test button already have, and here it
 *  buys something specific: the run is measured against the list it was
 *  STARTED with. If a second pump is left at the same address an hour later,
 *  the responder who was handed a list of two is not silently held responsible
 *  for three. So the rows ticked against are fetched AFTER the snapshot
 *  exists, not before — the list on screen beforehand is a preview, and it is
 *  as old as however long somebody stood reading it.
 *
 *  UNTICKED MEANS STILL THERE, AND THAT IS THE POINT OF THE WHOLE FLOW. §07:
 *  "the volunteer isn't going to wait around, so a missed item must be noticed
 *  by the system, not by memory." `still_out` comes back from the completion
 *  and is shown in place, with the dialog left open on top of it. A run that
 *  left a pump on a driveway is not a success message to be dismissed by the
 *  same click that saved it.
 *
 *  NOTHING HERE RE-CHECKS A PERMISSION. Every route it calls is gated on
 *  `equipment.mark_returned`, and the checklist read on `equipment.view`; the
 *  CALLER decides whether to draw the button that mounts this, and any screen
 *  in a position to offer a collection is one that listed what is out in the
 *  first place. A second check here would be a second definition of who may
 *  collect, and the two would drift.
 */

export function CollectionRun({
  placeId, placeLabel, incidentId, locations, onClose, onDone, onError,
}: {
  placeId: string
  placeLabel: string
  incidentId?: string | null
  locations: ShelfRow[]
  onClose: () => void
  onDone: (message: string) => void
  onError: (e: unknown, fallback: string) => void
}) {
  const [items, setItems] = useState<CollectionItem[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [runId, setRunId] = useState<string | null>(null)
  /** How many rows the SERVER put on the run, and WHICH. The count comes back
   *  from `startRun`; the ids come from the re-read taken immediately after,
   *  which is the closest this screen can stand to the server's snapshot. Both
   *  are held because they answer different questions -- the count says how
   *  big the run is, and only the ids can say whether a row on screen is ON
   *  it. */
  const [snapshot, setSnapshot] = useState(0)
  const [runIds, setRunIds] = useState<Set<string>>(() => new Set())
  /** Ticked, sent, and gone from the address by the time a failed save was
   *  investigated -- so they DID come back, and the server never got to say
   *  so. See the catch in `save`. Asset tags, because that is what the report
   *  is read in. */
  const [recovered, setRecovered] = useState<string[]>([])
  const [ticked, setTicked] = useState<string[]>([])
  const [result, setResult] = useState<RunResult | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Two shapes reach here -- the catalogue's one row per LOCATION, and the
  // stock view's one row per location AND type. Normalised to {id, name}, and
  // deduplicated, because the second shape would otherwise list one warehouse
  // once per kind of thing on its shelves and hand React the same key three
  // times over.
  const places = Array.from(new Map(
    locations
      .filter((l) => l.active !== false)
      .map((l) => {
        const id = l.id ?? l.location_id
        return [id, { id, name: l.name ?? l.location_name }] as const
      }),
  ).values()).filter((l) => !!l.id)

  const [where, setWhere] = useState(places[0]?.id ?? '')

  useReturnFocus()

  const { leave, backdrop } = useDialogGuard(
    () => {
      // Nothing is asked while a write is in flight or once the run has been
      // completed -- in both of those cases the close callback below decides
      // what leaving means, and a prompt would only be a second chance to
      // throw the report away.
      if (busy || result) return null
      return runId
        ? 'Close without saving? The collection stays open and nothing has '
          + 'been marked back.'
        : null
    },
    () => {
      // CLOSING AFTER A WRITE MUST RELOAD THE SCREEN BEHIND. Everything that
      // came back has changed state, so `onClose` alone would leave a list
      // still showing collected items as out -- the same lie the checklist
      // exists to prevent, one screen further along.
      if (busy) return
      if (result) { onDone(summary(result)); return }
      onClose()
    },
  )

  // The preview: what is open at that address right now. Depends on the
  // address ALONE -- `onError` is the caller's closure and is rebuilt on every
  // render of the screen behind, so depending on it would re-read the address
  // over and over while the dispatcher stands here reading it.
  useEffect(() => {
    let alive = true
    api.atPlace(placeId)
      .then((res) => { if (alive) setItems(res.items) })
      .catch((e) => {
        if (!alive) return
        setFailed(true)
        onError(e, 'Could not read what is out at that address.')
      })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeId])

  /** The sentence the caller puts on its own notice strip once this closes. */
  function summary(res: RunResult): string {
    // `recovered` comes FIRST because the server cannot report it. An earlier
    // save that failed part-way through still committed what it had returned,
    // and none of that reaches `collected` on the retry -- so a run that
    // recovered two pumps and then raced on a third would otherwise be
    // summarised as having collected nothing at all.
    const home = [...recovered, ...res.collected]
    if (home.length === 0 && res.still_out.length === 0) {
      return 'Nothing was left to collect — everything on the checklist had '
        + 'already been marked back.'
    }
    const back = home.length === 0
      ? 'Nothing was ticked, so nothing was marked back'
      : `${home.join(', ')} back in stock`
    return res.still_out.length === 0
      ? `${back}.`
      : `${back}. Still at ${placeLabel}: `
        + `${res.still_out.map((s) => s.asset_tag).join(', ')}.`
  }

  async function start() {
    setBusy(true)
    setNote(null)
    let run: { id: string; items: number }
    try {
      run = await api.startRun({
        place_id: placeId, incident_id: incidentId ?? null,
      })
    } catch (e) {
      // SAID HERE AS WELL AS THERE. `onError` writes to the screen's notice
      // strip, which is BEHIND the backdrop -- so the dispatcher would watch
      // "Starting..." flip back to "Start collection" and see no other change
      // at all. The realistic refusal is `start_run`'s "Nothing is out at that
      // address", when somebody else collected between this preview being read
      // and the button being pressed, and that is exactly the case where a
      // silent no-op is read as a broken button.
      setNote(e instanceof ApiError ? e.message
        : 'Could not start the collection. Nothing has been written.')
      onError(e, 'Could not start the collection.')
      setBusy(false)
      return
    }
    setRunId(run.id)
    setSnapshot(run.items)
    setTicked([])
    // READ AGAIN, NOW THAT THE SNAPSHOT EXISTS. Each row's `id` is the loan id
    // the completion ticks against, and of the two lists this is the one taken
    // closest to the moment the server wrote the run.
    try {
      const fresh = await api.atPlace(placeId)
      setItems(fresh.items)
      setRunIds(new Set(fresh.items.map((i) => i.id)))
    } catch (e) {
      // The run DID start, and the caller's error strip will show whatever the
      // server said about the re-read -- which does not mention that. Said
      // here because it changes what the list on screen means.
      setNote('The collection has started, but the checklist could not be '
        + 'read again. What is listed below is a moment older than the run.')
      onError(e, 'Could not reread the checklist.')
    } finally {
      setBusy(false)
    }
  }

  async function save() {
    if (!runId) return
    setBusy(true)
    setNote(null)
    try {
      const res = await api.completeRun(runId, {
        picked_up: ticked,
        location_id: where || null,
      })
      setResult(res)
      // A CLEAN RUN CLOSES ITSELF; ONE THAT LEFT SOMETHING BEHIND DOES NOT.
      // The whole reason the server returns `still_out` is that nobody is
      // going to remember it, so it is read here before this goes away.
      if (res.still_out.length === 0) onDone(summary(res))
    } catch (e) {
      onError(e, 'Could not complete the collection.')
      // A COLLECTION IS NOT ALL-OR-NOTHING, AND SAYING SO IS NOT OPTIONAL.
      // `complete_run` returns each ticked item in its own transaction --
      // deliberately, so that six items where the fourth fails do not roll
      // back the three that physically came back in the van. A refusal here
      // can therefore be sitting on top of items already in stock. The address
      // is read again and the ticks cut down to what is genuinely still open,
      // which is both the honest picture and the only retry that will not be
      // refused a second time over an item somebody already marked back.
      try {
        const fresh = await api.atPlace(placeId)
        const open = new Set(fresh.items.map((i) => i.id))
        // WHAT DID COME BACK, CAPTURED HERE OR LOST FOR GOOD. `complete_run`
        // returns each ticked loan in its own transaction and then stops at
        // the first refusal, so the items already committed are back in stock
        // and the browser never receives a `collected` list saying so. On the
        // retry they are in NEITHER list -- not collected, because that call
        // did not return them, and not `still_out`, because they are closed.
        // Three pumps ticked with the third racing would then finish with
        // "nothing was left to collect", for a run that recovered two.
        //
        // Ticked, sent, and no longer open at the address IS the definition of
        // came back, so it is read off the two lists rather than guessed.
        const gone = (items ?? [])
          .filter((i) => ticked.includes(i.id) && !open.has(i.id))
          .map((i) => i.asset_tag)
        if (gone.length) setRecovered((r) => [...r, ...gone])
        setItems(fresh.items)
        setTicked((t) => t.filter((id) => open.has(id)))
        setNote(
          (gone.length
            ? `${gone.join(', ')} did come back and ${gone.length === 1
                ? 'is' : 'are'} in stock. `
            : '')
          + 'The rest were not saved — each item is recorded on its own. The '
          + 'list has been re-read to what is still out at this address; tick '
          + 'again and save.')
      } catch {
        setNote('Some of the ticked items may already have been recorded back '
          + '— each one is saved on its own. The list could not be re-read, '
          + 'so check “out now” before ticking anything again.')
        // The sentence above already says the list cannot be trusted, and a
        // second error about the same failure would only bury it.
      }
    } finally {
      setBusy(false)
    }
  }

  const rows = items ?? []
  const all = rows.length > 0 && ticked.length === rows.length

  return (
    <div className="modalback" {...backdrop}>
      {/* This is the dialog with the long checklist, so it is the one where
          Tab wandering out into the live buttons behind it costs the most --
          a stray Enter on the screen underneath sends an item out again. */}
      <div className="modal wide" role="dialog" aria-modal="true"
           aria-label={`Collect from ${placeLabel}`}
           onClick={(e) => e.stopPropagation()}>
        <h3>Collect from {placeLabel}</h3>

        {result ? (
          <>
            <p className="blurb">
              Each item was recorded on its own, so this is what actually
              happened rather than one all-or-nothing answer.
            </p>
            {/* `recovered` beside `collected`, for the same reason `summary`
                merges them: an item returned by a save that then failed
                part-way through is in stock and appears in neither of the
                server's two lists on the retry. */}
            <div className="notice ok" role="status">
              {recovered.length + result.collected.length === 0
                ? 'Nothing was ticked, so nothing was marked back.'
                : `${recovered.length + result.collected.length} back in `
                  + `stock: ${[...recovered, ...result.collected].join(', ')}.`}
            </div>

            {result.still_out.length > 0 && (
              <>
                <h3>Still at {placeLabel}</h3>
                {result.still_out.map((s) => (
                  <div className="listrow" key={s.loan_id}>
                    <span className="mono">{s.asset_tag}</span>
                    <span className="nm">{s.type_name}</span>
                    <span className="tag due">still there</span>
                  </div>
                ))}
                <p className="footnote">
                  These were not ticked, so they stay open and keep showing in
                  “out now” until somebody brings them back. Nobody has to
                  remember them.
                </p>
              </>
            )}
          </>
        ) : (
          <>
            <p className="blurb">
              {runId
                ? 'Tick what came back in the van. This is the list the run '
                  + 'was started with — anything left here stays open and '
                  + 'keeps showing in “out now”.'
                : 'What is out at this address. Nothing is written until the '
                  + 'collection is started, and starting it fixes the list '
                  + 'this run is measured against.'}
            </p>

            {note && <div className="notice wait" role="alert">{note}</div>}

            {items === null ? (
              <div className="listempty">
                {failed
                  ? 'Could not read what is out at that address.'
                  : 'Reading…'}
              </div>
            ) : rows.length === 0 ? (
              <div className="listempty">Nothing is out at this address.</div>
            ) : rows.map((row) => (
              <div className="listrow" key={row.id}>
                {runId && (
                  // NOTHING IS TICKED IN ADVANCE. Pre-ticking would make "the
                  // volunteer brought everything back" indistinguishable from
                  // "nobody looked", and the expensive half of that is an item
                  // recorded in stock while it is really still on a driveway
                  // -- discovered by the next call that needs it.
                  <input
                    type="checkbox"
                    aria-label={`${row.asset_tag} came back`}
                    checked={ticked.includes(row.id)}
                    onChange={(e) => setTicked((t) => (
                      e.target.checked
                        ? [...t, row.id]
                        : t.filter((id) => id !== row.id)))}
                  />
                )}
                <span className="mono">{row.asset_tag}</span>
                <span className="nm">{row.type_name}</span>
                {row.incident_number && (
                  <span className="tag">{row.incident_number}</span>
                )}
                {/* `.dim`, not `.when`: theme.css defines `.when` only under
                    `.listrow.call`, `table.sheet` and `.ev`, so it would land
                    here as unstyled inline text imitating a dimmed date. The
                    same class-scoping trap as `.src.call`, read the other way
                    round. */}
                <span className="dim">left {day(row.released_at)}</span>
              </div>
            ))}

            {runId && rows.length > 1 && (
              // A CONVENIENCE, NEVER A DEFAULT. One press for the ordinary
              // case where everything came back, but it is a press somebody
              // makes -- which is exactly what pre-ticking the boxes would
              // not have been.
              <button
                className="ghost tiny nogrow"
                type="button"
                onClick={() => setTicked(all ? [] : rows.map((r) => r.id))}
              >
                {all ? 'Untick everything' : 'Tick everything'}
              </button>
            )}

            {runId && rows.some((r) => !runIds.has(r.id)) && (
              // ROWS, NOT A COUNT, and the difference is the whole accuracy of
              // this sentence. A bare `rows.length !== snapshot` also fires
              // when the list SHRINKS -- which is what happens after a save
              // that failed part-way through, because the items we did return
              // dropped out of the address. It would then tell the dispatcher
              // that something new had been released here, confidently and
              // wrongly, at the exact moment he is deciding what to re-tick.
              //
              // The run's own loan ids are known (the re-read taken the moment
              // it started), so what is asked is the real question: is there a
              // row here that is NOT on this run? A tick on one of those is
              // quietly ignored by the completion and would not even come back
              // in `still_out`.
              <p className="footnote warn">
                Something has been left at this address since the checklist was
                made. This run covers the {snapshot} item
                {snapshot === 1 ? '' : 's'} it was started with; anything newer
                stays out however it is ticked here, so check “out now”
                afterwards.
              </p>
            )}

            {runId && (
              <>
                <label>Where did it come back to
                  <select value={where} disabled={places.length === 0}
                          onChange={(e) => setWhere(e.target.value)}>
                    {places.map((l) => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                </label>
                {places.length === 0 && (
                  <p className="footnote warn">
                    There is no warehouse or vehicle for anything to come back
                    to. Unless this organization checks things in at the depot,
                    every tick will be refused until one is added in the
                    equipment catalogue.
                  </p>
                )}
                {ticked.length === 0 && rows.length > 0 && (
                  <p className="footnote">
                    Nothing is ticked. Saving now records that nothing came
                    back — every item on the list stays out and keeps showing
                    in “out now”, which is a real answer and not an error.
                  </p>
                )}
                {/* Who physically collected it is not asked for here. There is
                    no roster on this dialog to pick from, so
                    `equipment_loans.returned_by_member` and
                    `collection_items.ticked_by` are left NULL rather than
                    filled in with the
                    dispatcher who typed it -- which would read later as a
                    claim that he drove out there himself. */}
              </>
            )}
          </>
        )}

        <div className="modalrow">
          {result ? (
            <button className="primary" onClick={leave}>Done</button>
          ) : (
            <>
              {/* DISABLED WHILE A WRITE IS IN FLIGHT, because the guard
                  already refuses to close then -- Cancel, Escape and the
                  backdrop all do nothing at all mid-save, deliberately, so
                  that a `still_out` report cannot be dismissed before it
                  arrives. A control that looks live and does nothing is the
                  "the button did nothing" failure this repository names
                  elsewhere; saying it is unavailable is the honest half. */}
              <button className="ghost" disabled={busy} onClick={leave}>
                Cancel
              </button>
              {runId ? (
                <button className="primary" disabled={busy} onClick={save}>
                  {busy ? 'Saving…'
                    : ticked.length > 0 ? `Mark ${ticked.length} back` : 'Save'}
                </button>
              ) : (
                <button className="primary" disabled={busy || rows.length === 0}
                        onClick={start}>
                  {busy ? 'Starting…' : 'Start collection'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
